/**
 * ModuleRouteGuard — Phase 10D + 10J route-level enforcement.
 *
 * Wraps a route element. When the current tenant lacks the required
 * module(s), we redirect to the elegant LockedModulePage instead of
 * crashing with a blank screen or kicking the user back to the
 * dashboard. The locked page renders the upgrade CTA, the required
 * plan and a "Richiedi demo" mailto.
 *
 *   <Route
 *     path="/food-cost"
 *     element={
 *       <ModuleRouteGuard module="food_cost">
 *         <FoodCostPage />
 *       </ModuleRouteGuard>
 *     }
 *   />
 *
 * Properties:
 *   - module: single module id
 *   - anyOf: list of module ids — at least one required
 *   - allOf: list of module ids — all required
 *   - redirectTo: optional explicit override (defaults to /locked/:module)
 */
import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAccessMatrix } from "@/context/AccessMatrixContext";
import { toast } from "sonner";


export default function ModuleRouteGuard({
  module, anyOf, allOf, redirectTo, children,
}) {
  const location = useLocation();
  const { hasModule, hasAnyModule, hasAllModules, loading, isPlatform } = useAccessMatrix();

  let ok = isPlatform; // Platform users bypass module gating
  if (!ok) {
    if (module) ok = hasModule(module);
    else if (anyOf?.length) ok = hasAnyModule(anyOf);
    else if (allOf?.length) ok = hasAllModules(allOf);
    else ok = true;
  }

  // Show a soft toast in addition to redirecting (helps discovery).
  useEffect(() => {
    if (!loading && !ok) {
      const which = module || (anyOf || [])[0] || (allOf || [])[0];
      toast.message("Funzionalità non inclusa nel tuo piano", {
        description: which ? `Richiede il modulo "${which}"` : undefined,
        duration: 3500,
      });
    }
  }, [loading, ok, module, anyOf, allOf]);

  // Wait for the matrix to settle before deciding
  if (loading) return null;
  if (!ok) {
    // Choose a deterministic target so the LockedModulePage can render the
    // right upgrade content. Single-module guards go to /locked/{module};
    // for ANY/ALL guards we pick the first one to display in the upsell.
    const target =
      redirectTo
      || (module ? `/locked/${module}`
        : ((anyOf || [])[0] ? `/locked/${anyOf[0]}`
          : ((allOf || [])[0] ? `/locked/${allOf[0]}` : "/dashboard")));
    return (
      <Navigate
        to={target}
        replace
        state={{ from: location, denied_module: module || (anyOf || allOf || [])[0] }}
      />
    );
  }
  return children;
}
